import { ReactNode, useState } from 'react';

import { Typography, useDeviceType } from '@braskai/ui-kit';

import { Box, Stack } from '@mui/material';

import { PlansByCategory } from '@/widgets/pricing/pricingCards/lib/types';

import { MONTHS_IN_YEAR } from '@/shared/constants/common';
import { PaymentInterval, Plan, PlanCategory, Price } from '@/shared/types/Billing';

import { CardHeader } from './components/CardHeader';
import { DiscountChip } from './components/DiscountChip';
import { PriceBlock } from './components/PriceBlock';
import { CardButton } from './components/PriceBlock/components/CardButton';
import { PricingCardFeatures } from './components/PricingCardFeatures/PricingCardFeatures';
import { PRICING_CARD_DISCOUNT } from './constants/pricingCardDiscound';
import { usePricingCardState } from './hooks/usePricingCardState';
import { SContainer } from './styled';

// Keeps the bottom edge of every card aligned even when the CTA is hidden.
const CTA_SLOT_MIN_HEIGHT = 40;

type PricingCardProps = {
  chosenPlanId: null | string;
  plansByCategory: PlansByCategory;
  selectedPaymentPeriod: PaymentInterval;
  onActionButtonClick: (priceId: Price['id'], plan: Plan) => void;
};

export const PricingCard = ({
  chosenPlanId,
  plansByCategory,
  selectedPaymentPeriod,
  onActionButtonClick,
}: PricingCardProps) => {
  const { isMobile } = useDeviceType();
  // Mobile cards are collapsible accordions; open by default so all plan details are visible.
  const [isExpanded, setIsExpanded] = useState(true);

  const {
    activePlan,
    currentPeriodPrice,
    isButtonHidden,
    isCurrentPlan,
    isCustomPlan,
    isDisabled,
    isInitiallyActiveSubscription,
  } = usePricingCardState({ plansByCategory, selectedPaymentPeriod });

  const isEnterprise = activePlan.category === PlanCategory.Enterprise;
  const isCreatorPro = activePlan.category === PlanCategory.CreatorPro;
  const isBusiness = activePlan.category === PlanCategory.Business;

  // Annual plans grant the full year of minutes upfront; show that when Annual is selected.
  const monthlyMinutes = Number(activePlan.metadata.bundle_value);
  const isYearlySelected = selectedPaymentPeriod === PaymentInterval.Year;
  const minutesValue = isYearlySelected ? monthlyMinutes * MONTHS_IN_YEAR : monthlyMinutes;

  // RASK35 promo chip (yearly Creator / Creator Pro) — the price below already shows the promo math.
  const discountConfig = PRICING_CARD_DISCOUNT[activePlan.category as keyof typeof PRICING_CARD_DISCOUNT];
  const discountChipLabel =
    isYearlySelected && discountConfig
      ? `-${Math.round(discountConfig.year * 100)}% with code ${discountConfig.code}*`
      : null;

  const showFeatures = !isMobile || isExpanded;
  const features = <PricingCardFeatures isEnterprise={isEnterprise} pricingCardActivePlan={activePlan} />;

  const cardHeader = (
    <CardHeader
      isExpanded={isExpanded}
      isMobile={isMobile}
      isPriceActive={isCurrentPlan}
      plan={activePlan}
      onToggleExpand={() => setIsExpanded((prev) => !prev)}
    />
  );

  let ctaSlot: ReactNode;
  if (!isButtonHidden) {
    ctaSlot = (
      <CardButton
        chosenPlanId={chosenPlanId}
        isActiveSubscription={isInitiallyActiveSubscription}
        isCustomPlan={isCustomPlan}
        isPriceActive={isCurrentPlan}
        plan={activePlan}
        priceId={currentPeriodPrice?.id}
        onActionButtonClick={onActionButtonClick}
      />
    );
  } else if (isCurrentPlan) {
    ctaSlot = (
      <Stack
        sx={{
          border: '1px solid',
          borderColor: isEnterprise ? 'grey.300' : 'grey.200',
          borderRadius: 8,
          minHeight: CTA_SLOT_MIN_HEIGHT,
        }}
        alignItems="center"
        justifyContent="center"
      >
        <Typography color={isEnterprise ? 'common.white' : 'grey.700'} variant="body-2-md">
          Current plan
        </Typography>
      </Stack>
    );
  } else {
    ctaSlot = <Box sx={{ minHeight: CTA_SLOT_MIN_HEIGHT }} />;
  }

  return (
    <SContainer
      $isBusiness={isBusiness}
      $isCreatorPro={isCreatorPro}
      $isCurrentPlan={isCurrentPlan}
      $isDisabled={isDisabled}
      $isEnterprise={isEnterprise}
    >
      <Stack gap={24}>
        {isMobile ? (
          <>
            <Stack gap={8}>
              {cardHeader}
              {showFeatures && features}
            </Stack>
            <PriceBlock minutesValue={minutesValue} plan={activePlan} selectedPaymentPeriod={selectedPaymentPeriod} />
          </>
        ) : (
          <>
            {/* Figma: Enterprise "Custom" sits 4px under the subhead so it lines up with the other prices */}
            <Stack gap={isEnterprise ? 4 : 24}>
              {cardHeader}
              <PriceBlock minutesValue={minutesValue} plan={activePlan} selectedPaymentPeriod={selectedPaymentPeriod} />
            </Stack>
            {features}
          </>
        )}
      </Stack>

      {ctaSlot}

      {discountChipLabel && <DiscountChip label={discountChipLabel} />}
    </SContainer>
  );
};
